import { type RefObject, useCallback, useEffect, useState } from 'react';

import { FLOATING_HEADER_BREAKPOINT_ID, getDocumentTop } from 'shared/utils/floatingHeader';

/** Проверка на скролл — раз в 100 мс, как `lodashThrottle(check, 100)` монолита */
const SCROLL_THROTTLE_MS = 100;

interface IFloatingHeaderScroll {
  isVisible: boolean;
  activeIndex: number;
  /** Плавная прокрутка к цели пункта с поправкой на высоту шапки */
  scrollToTarget(targetId: string): void;
}

/** Не чаще раза в `wait` мс: первый вызов сразу, последний — по окончании интервала (как `lodash.throttle`) */
function throttle(callback: () => void, wait: number) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let isPending = false;

  const throttled = () => {
    if (timer) {
      isPending = true;

      return;
    }

    callback();
    timer = setTimeout(() => {
      timer = null;

      if (isPending) {
        isPending = false;
        throttled();
      }
    }, wait);
  };

  throttled.cancel = () => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = null;
    isPending = false;
  };

  return throttled;
}

const getBreakpoint = (): number | null => {
  const marker = document.getElementById(FLOATING_HEADER_BREAKPOINT_ID);

  return marker ? getDocumentTop(marker) : null;
};

/**
 * Видимость шапки и активный пункт навигации — `check` в `floating_header.html` монолита:
 * шапка видна, когда прокрутка дальше маркера минус высота шапки; активен последний пункт, чья цель
 * (минус высота шапки) уже выше прокрутки, первый — с нуля. Пункты без цели на странице пропускаются
 */
export function useFloatingHeaderScroll(
  headerRef: RefObject<HTMLElement | null>,
  targetIds: string[],
): IFloatingHeaderScroll {
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const targetsKey = targetIds.join();

  useEffect(() => {
    const header = headerRef.current;
    const breakpoint = getBreakpoint();
    // Пункты сравниваем по содержимому, а не по ссылке на массив
    const ids = targetsKey.split(',');

    if (!header || breakpoint === null) {
      return;
    }

    const check = () => {
      const height = header.scrollHeight;
      const scroll = window.scrollY;
      const threshold = (getBreakpoint() ?? breakpoint) - height;

      // При равенстве состояние не меняется, как в монолите
      setIsVisible(current => (scroll > threshold ? true : scroll < threshold ? false : current));

      let active = 0;

      for (let index = 1; index < ids.length; index++) {
        const target = document.getElementById(ids[index]);

        if (!target) {
          continue;
        }
        if (getDocumentTop(target) - height > scroll) {
          break;
        }

        active = index;
      }

      setActiveIndex(active);
    };

    const throttledCheck = throttle(check, SCROLL_THROTTLE_MS);

    check();
    window.addEventListener('scroll', throttledCheck, { passive: true });

    return () => {
      throttledCheck.cancel();
      window.removeEventListener('scroll', throttledCheck);
    };
  }, [headerRef, targetsKey]);

  const scrollToTarget = useCallback(
    (targetId: string) => {
      const target = document.getElementById(targetId);
      const header = headerRef.current;
      const breakpoint = getBreakpoint();

      if (!target || !header) {
        return;
      }

      const top = getDocumentTop(target);

      // Цели ниже маркера закрыла бы шапка — вычитаем её высоту
      window.scrollTo({
        top: breakpoint !== null && top > breakpoint ? top - header.scrollHeight : top,
        behavior: 'smooth',
      });
    },
    [headerRef],
  );

  return { isVisible, activeIndex, scrollToTarget };
}
